/**
 * RagServerUrlField — structured override control for `rag_config.server_url`.
 *
 * Phase 5 of the rag-deployment-config plan replaces the freeform Server URL
 * input with three radio modes:
 *
 *   1. Use deployment default — most common; server_url stays unset.
 *   2. Use a registered alias — for cross-environment Path B (the recommended
 *      cross-env story). Dropdown populated from `featureStore.ragExternalAliases`.
 *   3. Custom URL — Path A fallback. Permission-gated on
 *      `personas:override-server-url` (admin role by default). Has a warning
 *      because it requires CORS allowlist on the target RAG.
 *
 * The control infers the active mode from the current value. Missing/empty
 * → "default". Starts with `/rag-external/` → "alias". Otherwise → "custom".
 */

import { useMemo } from 'react'
import { AlertTriangle, Globe, Server } from 'lucide-react'

import { Input } from '@/components/ui'

interface RagServerUrlFieldProps {
  value?: string | null
  onChange: (value: string | undefined) => void
  /** Deployment-level base URL (display-only, shows the user what "default" resolves to). */
  deploymentBase: string
  /** Registered external alias names (cross-env Path B targets). */
  aliases: string[]
  /** Whether the caller has permissions to set a full custom URL (Path A). */
  canSetCustomUrl: boolean
}

type Mode = 'default' | 'alias' | 'custom'

function inferMode(value?: string | null): Mode {
  if (!value) return 'default'
  if (value.startsWith('/rag-external/')) return 'alias'
  return 'custom'
}

export function RagServerUrlField({
  value,
  onChange,
  deploymentBase,
  aliases,
  canSetCustomUrl,
}: RagServerUrlFieldProps) {
  const mode = useMemo(() => inferMode(value), [value])

  const selectedAlias = useMemo(() => {
    if (mode !== 'alias' || !value) return ''
    return value.replace(/^\/rag-external\//, '').split('/')[0]
  }, [mode, value])

  const setMode = (next: Mode) => {
    if (next === 'default') {
      onChange(undefined)
      return
    }
    if (next === 'alias') {
      // Pick the first alias by default if none selected
      const first = aliases[0]
      onChange(first ? `/rag-external/${first}` : undefined)
      return
    }
    // custom — keep an empty string so the input is editable
    onChange('')
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Server className="w-4 h-4 text-muted-foreground" />
        <label className="text-sm font-medium text-foreground">
          RAG Server
        </label>
      </div>

      <div className="space-y-2">
        {/* Mode 1: deployment default */}
        <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-muted">
          <input
            type="radio"
            name="rag-server-mode"
            checked={mode === 'default'}
            onChange={() => setMode('default')}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="text-sm font-medium">Use deployment default</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Routes to <code className="font-mono text-xs">{deploymentBase}</code>.
              Recommended for same-environment personas.
            </div>
          </div>
        </label>

        {/* Mode 2: registered alias */}
        {aliases.length > 0 && (
          <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-muted">
            <input
              type="radio"
              name="rag-server-mode"
              checked={mode === 'alias'}
              onChange={() => setMode('alias')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="text-sm font-medium flex items-center gap-2">
                <Globe className="w-3 h-3" />
                Use a registered alias (cross-environment)
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 mb-2">
                Routes through this deployment's nginx to a pre-registered
                cross-environment RAG. No CORS coordination required on the target.
              </div>
              {mode === 'alias' && (
                <select
                  className="w-full text-sm rounded-sm border border-border px-2 py-1 bg-background"
                  value={selectedAlias}
                  onChange={(e) => onChange(`/rag-external/${e.target.value}`)}
                >
                  {aliases.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </label>
        )}

        {/* Mode 3: custom URL (permission-gated) */}
        {canSetCustomUrl && (
          <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-muted">
            <input
              type="radio"
              name="rag-server-mode"
              checked={mode === 'custom'}
              onChange={() => setMode('custom')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-3 h-3 text-warning" />
                Custom URL (advanced)
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 mb-2">
                Use only when registering an alias on this deployment is not
                possible. The browser fetches cross-origin, so the target RAG
                must include this app's origin in its <code className="font-mono">CORS_ORIGINS</code>.
              </div>
              {mode === 'custom' && (
                <Input
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value || undefined)}
                  placeholder="https://my-rag.example.com"
                />
              )}
            </div>
          </label>
        )}
      </div>
    </div>
  )
}
